import React, { useState, useEffect } from "react";
import stylez from "./ShipmentModal.module.css";
import Modal from "../../../Components/Shared/Modal/Modal";
import Select from "react-select";
import axiosInstance from "../../../Configuration/axiosInstance";

export const AddModal = (props) => {
  const { warehouses } = props;
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [comment, setComment] = useState("");
  const [msg, setMsg] = useState("");
  const [editableContainers, setEditableContainers] = useState([]);
  const [activeContainer, setActiveContainer] = useState(0);

  // ✅ NEW: Initialize containers from shipment data
  useEffect(() => {
    if (props.i) {
      let containers = [];

      // New format: multiple containers
      if (props.i.containers && props.i.containers.length > 0) {
        containers = props.i.containers.map((container, index) => ({
          id: container._id || index,
          containerNumber: container.containerNumber,
          vessel: container.vessel,
          sealNumber: container.sealNumber,
          items: container.items.map((item) => ({
            product: item.product,
            quantity: item.quantity,
            originalQuantity: item.quantity,
          })),
        }));
      } else {
        // Old format: single container (backward compatibility)
        containers = [
          {
            id: 0,
            containerNumber: props.i.containerNumber || "Legacy Container",
            vessel: props.i.Vessel || "N/A",
            sealNumber: props.i.sealNumber || "N/A",
            items: (props.i.items || []).map((item) => ({
              product: item.product,
              quantity: item.quantity,
              originalQuantity: item.quantity,
            })),
          },
        ];
      }

      setEditableContainers(containers);
    }
  }, [props.i]);

  const warehouseOptions = warehouses?.map((i) => ({
    value: i._id,
    label: i.name,
  }));

  // ✅ NEW: Handle quantity change for specific container and item
  const handleQuantityChange = (containerIndex, itemIndex, newQuantity) => {
    const updatedContainers = [...editableContainers];
    updatedContainers[containerIndex].items[itemIndex].quantity = Math.max(
      0,
      parseInt(newQuantity) || 0
    );
    setEditableContainers(updatedContainers);
  };

  // ✅ NEW: Get total items across all containers
  const getTotalItems = () => {
    return editableContainers.reduce((total, container) => {
      return total + container.items.length;
    }, 0);
  };

  // ✅ NEW: Get total quantity across all containers
  const getTotalQuantity = () => {
    return editableContainers.reduce((total, container) => {
      return (
        total +
        container.items.reduce((containerTotal, item) => {
          return containerTotal + item.quantity;
        }, 0)
      );
    }, 0);
  };

  // ✅ UPDATED: Handle transfer with new container structure
  const handleTransfer = () => {
    if (!selectedWarehouse) {
      setMsg("Please select a warehouse before transferring.");
      return;
    }

    // Prepare transfer payload with new format
    const transferPayload = {
      warehouseId: selectedWarehouse.value,
      containers: editableContainers.map((container) => ({
        containerNumber: container.containerNumber,
        vessel: container.vessel,
        sealNumber: container.sealNumber,
        items: container.items.map((item) => ({
          product: item.product._id,
          quantity: item.quantity,
        })),
      })),
      comment:
        comment ||
        `Stock transfer from ${editableContainers.length} container(s)`,
    };

    setMsg("Updating.... Please wait");

    axiosInstance
      .post(`shipments/transferShipment/${props.i?._id}`, transferPayload)
      .then((response) => {
        setMsg(response.data.message);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      })
      .catch((error) => {
        console.error("Error transferring stock:", error);
        setMsg("Error transferring stock. Please try again.");
      });
  };

  // ✅ NEW: Check if transfer is disabled
  const isTransferDisabled = () => {
    return (
      props.i?.transfered ||
      !selectedWarehouse ||
      (props.i?.eta && new Date(props.i?.eta) > new Date()) ||
      getTotalQuantity() === 0
    );
  };

  return (
    <Modal show={props.show} setShow={props.setShow}>
      <div className={stylez.modalContainer}>
        <h1 className={stylez.modalHeader}>
          🚢 Transfer Stock - BL: {props.i?.blNumber}
        </h1>

        {/* ✅ NEW: Shipment Summary */}
        <div className={stylez.shipmentSummary}>
          <div className={stylez.summaryGrid}>
            <div className={stylez.summaryItem}>
              <span className={stylez.summaryLabel}>Total Containers:</span>
              <span className={stylez.summaryValue}>
                {editableContainers.length}
              </span>
            </div>
            <div className={stylez.summaryItem}>
              <span className={stylez.summaryLabel}>Total Items:</span>
              <span className={stylez.summaryValue}>{getTotalItems()}</span>
            </div>
            <div className={stylez.summaryItem}>
              <span className={stylez.summaryLabel}>Total Quantity:</span>
              <span className={stylez.summaryValue}>{getTotalQuantity()}</span>
            </div>
            <div className={stylez.summaryItem}>
              <span className={stylez.summaryLabel}>ETA:</span>
              <span className={stylez.summaryValue}>
                {props.i?.eta
                  ? new Date(props.i.eta).toLocaleDateString("en-GB")
                  : "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* ✅ NEW: Container Navigation */}
        {editableContainers.length > 1 && (
          <div className={stylez.containerTabs}>
            <h3 className={stylez.tabsTitle}>Select Container:</h3>
            <div className={stylez.tabsContainer}>
              {editableContainers.map((container, index) => (
                <button
                  key={container.id}
                  className={`${stylez.containerTab} ${
                    activeContainer === index ? stylez.activeTab : ""
                  }`}
                  onClick={() => setActiveContainer(index)}
                >
                  📦 Container {index + 1}
                  <span className={stylez.tabInfo}>
                    ({container.items.length} items)
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ✅ NEW: Active Container Details */}
        {editableContainers[activeContainer] && (
          <div className={stylez.containerSection}>
            <div className={stylez.containerHeader}>
              <h3 className={stylez.containerTitle}>
                🚢 Container {activeContainer + 1} Details
              </h3>
              <div className={stylez.containerInfo}>
                <div className={stylez.infoItem}>
                  <span className={stylez.infoLabel}>Container #:</span>
                  <span className={stylez.infoValue}>
                    {editableContainers[activeContainer].containerNumber}
                  </span>
                </div>
                <div className={stylez.infoItem}>
                  <span className={stylez.infoLabel}>Vessel:</span>
                  <span className={stylez.infoValue}>
                    {editableContainers[activeContainer].vessel}
                  </span>
                </div>
                <div className={stylez.infoItem}>
                  <span className={stylez.infoLabel}>Seal #:</span>
                  <span className={stylez.infoValue}>
                    {editableContainers[activeContainer].sealNumber || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Products Table for Active Container */}
            <div className={stylez.modalContent}>
              <table className={stylez.modalTable}>
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Original Qty</th>
                    <th>Transfer Qty</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {editableContainers[activeContainer].items?.map(
                    (item, itemIndex) => (
                      <tr key={itemIndex}>
                        <td className={stylez.productCell}>
                          <div className={stylez.productInfo}>
                            <span className={stylez.productName}>
                              {item?.product.productName}
                            </span>
                            <span className={stylez.productCode}>
                              Code: {item?.product.productCode || "N/A"}
                            </span>
                          </div>
                        </td>
                        <td className={stylez.quantityCell}>
                          <span className={stylez.originalQty}>
                            {item?.originalQuantity}
                          </span>
                        </td>
                        <td className={stylez.inputCell}>
                          <input
                            type="number"
                            min="0"
                            max={item?.originalQuantity}
                            value={item?.quantity}
                            onChange={(e) =>
                              handleQuantityChange(
                                activeContainer,
                                itemIndex,
                                e.target.value
                              )
                            }
                            className={stylez.quantityInput}
                          />
                        </td>
                        <td className={stylez.unitCell}>
                          <span className={stylez.unit}>
                            {item?.product.unit || "N/A"}
                          </span>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Warehouse Selection */}
        <div className={stylez.warehouseSection}>
          <label className={stylez.warehouseLabel}>Select Warehouse:</label>
          <Select
            options={warehouseOptions}
            value={selectedWarehouse}
            onChange={(selectedOption) => setSelectedWarehouse(selectedOption)}
            className={stylez.warehouseSelect}
            placeholder="Choose a warehouse..."
            isSearchable
          />
        </div>

        {/* Comments Section */}
        <div className={stylez.commentsSection}>
          <label className={stylez.commentsLabel}>
            Transfer Comments (Optional):
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any notes about this transfer..."
            className={stylez.commentsTextarea}
            rows={3}
          />
        </div>

        {/* Status Message */}
        {msg && (
          <div className={stylez.messageContainer}>
            <div
              className={`${stylez.message} ${
                msg.includes("Error")
                  ? stylez.errorMessage
                  : stylez.successMessage
              }`}
            >
              {msg}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className={stylez.actionButtons}>
          <button
            disabled={isTransferDisabled()}
            className={`${stylez.transferBtn} ${
              isTransferDisabled() ? stylez.disabledBtn : ""
            }`}
            onClick={handleTransfer}
            title={
              props.i?.transfered
                ? "This shipment has already been transferred"
                : !selectedWarehouse
                ? "Please select a warehouse"
                : props.i?.eta && new Date(props.i?.eta) > new Date()
                ? "Cannot transfer before ETA"
                : "Transfer stock to selected warehouse"
            }
          >
            Transfer Stock
          </button>

          <button
            className={stylez.closeBtn}
            onClick={() => props.setShow(false)}
          >
            ❌ Close
          </button>
        </div>

        {/* Transfer Status */}
        {props.i?.transfered && (
          <div className={stylez.transferredBanner}>
            ✅ This shipment has already been transferred
          </div>
        )}
      </div>
    </Modal>
  );
};
